// game/client/src/day-night-visual.ts
//
// Hand-rolled day/night lighting lerp — the "7-value lerp" from
// docs/design/14-rendering.md §"Day/night transition". A single per-frame
// interpolation over sun colour + intensity, hemi sky/ground colours + intensity,
// and fog/background colour + fog density, driven by `nightness()` (0=day, 1=night).
//
// DAY keyframe = the CURRENT shipped values from main.ts (tuned across visual
// passes v1/v2), NOT the original lighting-spec table — so turning the lerp on
// leaves high-noon looking exactly as it does today. NIGHT keyframe = the
// lighting-shaders-research spec §1 "KTS-Haven night" table.
//
// No tween library, no EffectComposer; linear lerp reads fine over the 20s band.

import * as THREE from "three";
import { nightness, twilightWarmth } from "./day-night-cycle";
import type { Horizon } from "./world/horizon";

interface LightKeyframe {
  sunColor: number;
  sunIntensity: number;
  skyColor: number;
  groundColor: number;
  hemiIntensity: number;
  fogColor: number;
  fogDensity: number;
}

// Mirrors the static setup in main.ts. Keep in sync if those initialisers change.
//
// Primal re-grade (2026-06-24, branch feat/stone-age-assets): DAY shifts from the
// old cool-temperate "KTS-Haven" look (cool-blue hemi sky, pale-blue fog) toward
// the Tartakovsky/loading-screen target — a warm amber-gold key light, a warm
// hazy sky fill, an ochre ground bounce, and a dusty-warm horizon. The RED-sun
// drama lives in the sky/fog (and a future dusk keyframe), NOT in the key light:
// the DirectionalLight stays a readable warm-gold so units never sink into a red
// murk (Aviv's #1 constraint — keep it readable; work colour, not darkness).
const DAY: LightKeyframe = {
  sunColor: 0xffc176,    // warm amber-gold key — readable, kills the cool cast
  sunIntensity: 0.95,
  skyColor: 0xe0c79c,    // warm hazy sky fill (was cool blue 0x9bbed8)
  groundColor: 0xa8773e, // ochre ground bounce (was 0x8c6a45) — earths undersides
  hemiIntensity: 1.05,
  fogColor: 0xd7b483,    // dusty-warm horizon haze (was pale blue 0xc8d8e4)
  fogDensity: 0.008,
};

// Night = ATMOSPHERE, not a readability wall (Aviv, 2026-06-11; reinforced
// 2026-06-12 — units still too hard to see). The player must still see and move
// freely at night, and UNITS in particular must pop; it reads as a cool moonlit
// mood, NOT darkness. Tuned by reviewing rendered screenshots, not by trusting
// the numbers — an earlier hemi 0.62 / bg 0x1c2440 looked fine on paper but
// rendered as trees melting into black. This deliberately diverges UP hard from
// the lighting-research spec §1 night row (hemi 0.18 / bg 0x0a0e1c).
//
// 2026-06-12 readability pass: the prior night row kept hemi *intensity* near
// day but the sky/ground *colours* were dark+desaturated (sky 0x5a6e9e, ground
// 0x44485a), so the hemisphere fill tinted characters a murky blue-grey and
// units sank into the field. Units are lit almost entirely by this hemi fill
// (the moon is a low grazing key), so the readability lever is the fill COLOUR
// LUMINANCE, not its intensity. Raised sky/ground luminance ~40% (kept the cool
// blue hue), and lifted the moon a notch for rim definition. Fog stays the same
// blue so view distance + the night read (blue tint) are unchanged.
const NIGHT: LightKeyframe = {
  sunColor: 0x9fb2d6,   // moonlight — cool, but bright enough to read by
  sunIntensity: 0.9,
  skyColor: 0x7d92c2,   // brighter cool blue — fill that keeps units legible
  groundColor: 0x5e6478, // lighter blue-grey bounce so under-lit faces don't go black
  hemiIntensity: 1.1,
  fogColor: 0x36406a,
  fogDensity: 0.008,
};

// DUSK / DAWN twilight keyframe — the Tartakovsky "red sun" showpiece (Aviv:
// "I don't see anything red as far as lighting" + "sun gets redder at certain
// points"). NOT a third point on the day↔night line: it's blended OVER the base
// by a `twilight` factor that peaks at the MIDDLE of each transition band (the
// 20s dusk tail of day, the 20s dawn tail of night) and is zero through the day
// body and night body. So midday stays golden, night stays cool, and twice a
// cycle the KEY LIGHT itself swings fiery orange-red with a crimson hazy horizon.
// Intensity stays high (1.0) so the red reads as dramatic warm light, not darkness
// (keep-it-readable constraint). This is what makes the sun feel like it travels
// and reddens without physically rotating it (rotating shadows read wrong at MOBA
// zoom — a locked call in 14-rendering.md).
const DUSK: LightKeyframe = {
  sunColor: 0xff6330,   // fiery orange-red key — the visible "red sun" light
  sunIntensity: 1.0,
  skyColor: 0xd98a4e,   // warm orange sky fill
  groundColor: 0x8a4a2e, // warm red-brown bounce
  hemiIntensity: 1.0,
  fogColor: 0xb5502e,   // crimson-orange hazy horizon (the bruised dusk sky)
  fogDensity: 0.011,    // a touch thicker — dusty atmosphere
};

export interface DayNightVisual {
  /** Apply the lighting for the given server cycle state. Cheap; call per frame. */
  apply(phase: number, cycleElapsed: number): void;
}

export function createDayNightVisual(opts: {
  scene: THREE.Scene;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  /** Optional horizon (sky dome + apron + ridge); recoloured off the same lerp. */
  horizon?: Horizon | null;
}): DayNightVisual {
  const { scene, sun, hemi, horizon } = opts;

  // Pre-built Color instances so per-frame apply() allocates nothing.
  const daySun = new THREE.Color(DAY.sunColor);
  const nightSun = new THREE.Color(NIGHT.sunColor);
  const daySky = new THREE.Color(DAY.skyColor);
  const nightSky = new THREE.Color(NIGHT.skyColor);
  const dayGround = new THREE.Color(DAY.groundColor);
  const nightGround = new THREE.Color(NIGHT.groundColor);
  const dayFog = new THREE.Color(DAY.fogColor);
  const nightFog = new THREE.Color(NIGHT.fogColor);
  // Dusk/dawn twilight keyframe colours (blended over the base — see DUSK above).
  const duskSun = new THREE.Color(DUSK.sunColor);
  const duskSky = new THREE.Color(DUSK.skyColor);
  const duskGround = new THREE.Color(DUSK.groundColor);
  const duskFog = new THREE.Color(DUSK.fogColor);

  const fog = scene.fog as THREE.FogExp2 | null;
  const bg = scene.background instanceof THREE.Color ? scene.background : null;
  const _horizonScratch = new THREE.Color();

  function apply(phase: number, cycleElapsed: number): void {
    const t = nightness(phase, cycleElapsed);
    // Twilight warmth: a BROAD golden arc (day-night-cycle.ts) — full warm-red at
    // the dawn/dusk edges, easing to warm-gold at midday — so the dusk/dawn look
    // occupies much of the day, not just the 20s transition. Layered OVER the
    // day↔night base.
    const twilight = twilightWarmth(phase, cycleElapsed);

    sun.color.copy(daySun).lerp(nightSun, t).lerp(duskSun, twilight);
    sun.intensity = THREE.MathUtils.lerp(
      THREE.MathUtils.lerp(DAY.sunIntensity, NIGHT.sunIntensity, t), DUSK.sunIntensity, twilight);

    hemi.color.copy(daySky).lerp(nightSky, t).lerp(duskSky, twilight);
    hemi.groundColor.copy(dayGround).lerp(nightGround, t).lerp(duskGround, twilight);
    hemi.intensity = THREE.MathUtils.lerp(
      THREE.MathUtils.lerp(DAY.hemiIntensity, NIGHT.hemiIntensity, t), DUSK.hemiIntensity, twilight);

    if (fog) {
      fog.color.copy(dayFog).lerp(nightFog, t).lerp(duskFog, twilight);
      fog.density = THREE.MathUtils.lerp(
        THREE.MathUtils.lerp(DAY.fogDensity, NIGHT.fogDensity, t), DUSK.fogDensity, twilight);
    }
    // Background tracks fog colour so the horizon stays seamless.
    if (bg) bg.copy(dayFog).lerp(nightFog, t).lerp(duskFog, twilight);
    // Horizon (sky dome / apron / ridge) shares the same horizon = fog colour.
    if (horizon) {
      _horizonScratch.copy(dayFog).lerp(nightFog, t).lerp(duskFog, twilight);
      horizon.update(_horizonScratch, t);
    }
  }

  return { apply };
}
